import { Injectable } from '@angular/core';
import { AuthUser } from '@app/account/account-api.model';
import { ColumnConfiguration } from '@app/shared/components/column-configurator/column-configurator.model';
import { AuthService } from '@app/shared/services/auth.service';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { InternalTagRatingTypes } from '@app/tags/tags';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ProfileFacet,
  ProfileFacetValue,
  ProfileFilter,
} from '../components/profile-filter-bar/profile-filter-bar.component';
import { tagsHaveRatingsForType } from '@app/shared/utils/tag-helpers';
import { TagsService } from '@app/tags/services/tags.service';
import { TagFlagsService } from '@app/tags/services/tag-flags.service';

export interface UserProfileSkillsData {
  // Reason is the tag the changed and caused the tags to be refreshed from the
  // server. For the initial load on subscribe, it's undefined.
  reason?: TagsApi.TagDetails;

  // Data is another single-shot observable that will emit the data when it's
  // ready. We need to do this in two steps because code that uses it needs to
  // know when the data is about to change to animate the transitions.
  data: Observable<{
    focus: TagsApi.TagDetails[];
    required: TagsApi.TagDetails[];
    other: TagsApi.TagDetails[];
    filter?: ProfileFilter;
  }>;
}

@Injectable({
  providedIn: 'root',
})
export class ProfileSkillsService {
  public i18n = this.translate.instant([
    'SkillRatingsChart_DegreedCertified',
    'SkillRatingsChart_ManagerRatings',
    'dgTagRating_PeerRatings',
    'SkillRatingsChart_SelfRatings',
    'SkillRatingsChart_Evaluations',
    'dgTagRating_RatingType',
    'dgTagRating_SelfAssessment',
    'dgTagRating_ManagerAssessment',
    'dgTagRating_PeerRatings',
    'dgTagRating_Evaluation',
    'dgTagRating_SkillCertification',
    'PersonalSkillView_SkillTarget',
    'dgProfileOverview_SkillSignalsTitle',
    'dgTagRating_TotalRatings',
  ]);
  constructor(
    private tagsService: TagsService,
    private translate: TranslateService,
    private authService: AuthService,
    private tagRatingService: TagRatingService,
    private tagFlagsService: TagFlagsService
  ) {}

  public getUserProfileSkills(
    profileKey: number
  ): Observable<UserProfileSkillsData> {
    return new Observable<TagsApi.TagDetails>((subscriber) => {
      // Emit when we get a new subscriber
      subscriber.next();

      // Also emit when we see the userTagsModified event
      const listener = this.tagsService.userTagsModified.subscribe(
        (payload) => {
          subscriber.next(payload?.tag);
        }
      );

      // Use the listener as the unsubscribe function for the whole observable
      return listener;
    }).pipe(
      map((reason: TagsApi.TagDetails) => {
        return {
          reason,
          data: this.tagsService.getUserProfileTags(profileKey).pipe(
            map(
              ({
                focus,
                required,
                other,
              }: {
                focus: TagsApi.TagDetails[];
                required: TagsApi.TagDetails[];
                other: TagsApi.TagDetails[];
              }) => {
                const allTags = [...focus, ...required, ...other];

                const filter = {
                  searchTerm: '',
                  isActive: false,
                  facets: [
                    {
                      id: 'RatingType',
                      name: this.i18n.dgTagRating_RatingType,
                      values: this.getOrgRatingTypesOptions(
                        this.authService.authUser,
                        allTags
                      ),
                    },
                  ],
                };
                filter.facets.forEach((facet) => {
                  this.setFacetCounts(facet, allTags as TagsApi.FilteredTag[]);
                });

                return {
                  focus,
                  required,
                  other,
                  filter,
                };
              }
            )
          ),
        };
      })
    );
  }

  public doSearch(
    skills: TagsApi.FilteredTag[],
    filter: ProfileFilter
  ): { data: TagsApi.FilteredTag[]; filter: ProfileFilter } {
    skills.forEach((skill) => {
      skill.show = this.filterBySearchType(filter.searchTerm, skill);
    });

    const filteredSkills = skills.filter((skill) => skill.show);
    const ratingTypeFacet = filter.facets.find(
      (facet) => facet.id === 'RatingType'
    );

    filter.facets.forEach((facet) =>
      this.setFacetCounts(facet, filteredSkills)
    );

    skills
      .filter((skill) => skill.show)
      .forEach((skill) => {
        skill.show = this.filterSkillByRatingType(ratingTypeFacet, skill);
      });

    return { data: skills, filter };
  }

  public getOrgRatingTypesOptions(
    authUser: AuthUser,
    tags: TagsApi.Tag[]
  ): ProfileFacetValue[] {
    const types = [
      {
        id: 'Self',
        name: this.i18n.SkillRatingsChart_SelfRatings,
        count: 0,
        filter: false,
      },
    ];

    if (!authUser) {
      return types;
    }

    const {
      userHasPeerRatedSkills,
      userHasManagerRatedSkills,
      userHasEvaluatedSkills,
      userHasCredentialedSkills,
    } = this.setColumnVisibilityFlags(tags);

    // Only show rating types available to the org
    if (
      (authUser?.canRequestPeerRating &&
        this.tagFlagsService.showPeerRatings) ||
      userHasPeerRatedSkills
    ) {
      types.push({
        id: 'Peer',
        name: this.i18n.dgTagRating_PeerRatings,
        count: 0,
        filter: false,
      });
    }
    if (authUser?.canRequestManagerRating || userHasManagerRatedSkills) {
      types.push({
        id: 'Manager',
        name: this.i18n.SkillRatingsChart_ManagerRatings,
        count: 0,
        filter: false,
      });
    }
    if (authUser?.canEvaluateSkills || userHasEvaluatedSkills) {
      types.push({
        id: 'Evaluation',
        name: this.i18n.SkillRatingsChart_Evaluations,
        count: 0,
        filter: false,
      });
    }
    if (userHasCredentialedSkills) {
      types.push({
        id: 'Credential',
        name: this.i18n.SkillRatingsChart_DegreedCertified,
        count: 0,
        filter: false,
      });
    }

    return types;
  }

  /**
   * Update the counts on the facet using the skills passed in.
   * SIDE EFFECT - this will directly update the facet.
   * @param facet facet to update
   * @param skills the skill list to use to update the count
   */
  public setFacetCounts(facet: ProfileFacet, skills: TagsApi.FilteredTag[]) {
    // reset count
    facet.values.forEach((val) => (val.count = 0));

    skills.forEach((skill) => {
      facet.values.forEach((val) => {
        if (facet.id === 'RatingType') {
          if (skill.ratings.some((rating) => rating.type === val.id)) {
            val.count++;
          }
        }
      });
    });
  }

  public filterBySearchType(searchTerm: string, skill: TagsApi.FilteredTag) {
    return searchTerm
      ? skill.title.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1
      : true;
  }

  public filterSkillByRatingType(
    ratingTypeFacet: ProfileFacet,
    skill: TagsApi.FilteredTag
  ): boolean {
    const activeFilters = ratingTypeFacet.values.filter((val) => val.filter);
    return activeFilters.length
      ? skill.ratings.some((rating) => {
          return activeFilters.some((srt) => srt.id === rating.type);
        })
      : true;
  }

  public setColumnVisibilityFlags(tags: TagsApi.Tag[]): {
    [key: string]: boolean;
  } {
    const { self, manager, peer, evaluation, credential } =
      InternalTagRatingTypes;

    return {
      /** Does the user have at least one self rating? */
      hasSelfRating: tagsHaveRatingsForType(tags, self),
      /** Does the user have at least one Manager rating */
      hasManagerRatings: tagsHaveRatingsForType(tags, manager),
      /** Does the user have at least one Peer rating */
      hasPeerRatings: tagsHaveRatingsForType(tags, peer),
      /** Does the user have at least one Manager rated tag? */
      userHasManagerRatedSkills: tagsHaveRatingsForType(tags, manager),
      /** Does the user have at least one Peer rated tag? */
      userHasPeerRatedSkills: tagsHaveRatingsForType(tags, peer),
      /** Does the user have at least one tag with `Manager` available? */
      managerRatingAvailable: this.tagRatingService.tagsHaveRatingTypeAvailable(
        tags,
        manager
      ),
      /** Does the user have at least one tag with `Peer` available? */
      peerRatingAvailable: this.tagRatingService.tagsHaveRatingTypeAvailable(
        tags,
        peer
      ),
      /** Does the user have at least one Evaluated (Skill Review) tag? */
      userHasEvaluatedSkills: tagsHaveRatingsForType(tags, evaluation),
      /** Does the user have at least one tag with `Evaluation` available? */
      evaluationIsAvailable: this.tagRatingService.tagsHaveRatingTypeAvailable(
        tags,
        evaluation
      ),
      /** Does the user have at least one Credentialed (Skill Certification) tag? */
      userHasCredentialedSkills: tagsHaveRatingsForType(tags, credential),
    };
  }

  public getProfileSkillsRatingTypeColumns(
    tags: TagsApi.Tag[],
    isOwner: boolean,
    canUserOptIntoTeams: boolean,
    showSignalsColumn: boolean
  ): Partial<ColumnConfiguration>[] {
    const columns: { type: InternalTagRatingTypes; label: string }[] = [];

    const {
      hasSelfRating,
      hasPeerRatings,
      hasManagerRatings,
      userHasManagerRatedSkills,
      userHasPeerRatedSkills,
      managerRatingAvailable,
      peerRatingAvailable,
      userHasEvaluatedSkills,
      evaluationIsAvailable,
      userHasCredentialedSkills,
    } = this.setColumnVisibilityFlags(tags);
    const {
      self,
      peer,
      manager,
      evaluation,
      credential,
      target,
      signals,
      total,
    } = InternalTagRatingTypes;

    // NOTE - Ordering is explicit:

    // Self
    if (isOwner || hasSelfRating) {
      columns.push({
        type: self,
        label: this.i18n.dgTagRating_SelfAssessment,
      });
    }

    // Peer
    if (
      (isOwner &&
        ((this.authService.authUser?.canRequestPeerRating &&
          this.tagFlagsService.showPeerRatings &&
          peerRatingAvailable) ||
          userHasPeerRatedSkills)) ||
      hasPeerRatings
    ) {
      columns.push({
        type: peer,
        label: this.i18n.dgTagRating_PeerRatings,
      });
    }

    // Manager
    if (
      (isOwner &&
        ((this.authService.authUser?.canRequestManagerRating &&
          managerRatingAvailable) ||
          userHasManagerRatedSkills)) ||
      hasManagerRatings
    ) {
      columns.push({
        type: manager,
        label: this.i18n.dgTagRating_ManagerAssessment,
      });
    }

    // Skill Review
    if (
      this.authService.authUser?.canEvaluateSkills ||
      userHasEvaluatedSkills ||
      evaluationIsAvailable
    ) {
      columns.push({
        type: evaluation,
        label: this.i18n.dgTagRating_Evaluation,
      });
    }

    // Certification
    if (userHasCredentialedSkills) {
      columns.push({
        type: credential,
        label: this.i18n.dgTagRating_SkillCertification,
      });
    }

    // Target
    if (canUserOptIntoTeams && isOwner) {
      columns.push({
        type: target,
        label: this.i18n.PersonalSkillView_SkillTarget,
      });
    }

    // Total Ratings
    columns.push({
      type: total,
      label: this.i18n.dgTagRating_TotalRatings,
    });

    // Skill signals column
    if (showSignalsColumn) {
      columns.push({
        type: signals,
        label: this.i18n.dgProfileOverview_SkillSignalsTitle,
      });
    }

    return columns;
  }
}
